import React, { useState, useEffect } from 'react';
import saasPlatformService from '../../services/saasPlatformService';
import './SaasPlatform.css';

const PlatformAudit = () => {
  // Navigation tabs
  const [activeTab, setActiveTab] = useState('streams'); // 'streams' | 'checkpoints' | 'incidents' | 'logs'

  // Tab 1: Streams & Verification
  const [streams, setStreams] = useState([]);
  const [streamsLoading, setStreamsLoading] = useState(true);
  const [streamError, setStreamError] = useState(null);
  const [selectedVerificationReport, setSelectedVerificationReport] = useState(null);
  const [verifyingStreamId, setVerifyingStreamId] = useState(null);

  // Tab 2: Checkpoints
  const [checkpoints, setCheckpoints] = useState([]);
  const [checkpointsLoading, setCheckpointsLoading] = useState(false);
  const [showCheckpointModal, setShowCheckpointModal] = useState(false);
  const [checkpointStreamId, setCheckpointStreamId] = useState('PLATFORM_ROOT');
  const [checkpointNotes, setCheckpointNotes] = useState('');
  const [submittingCheckpoint, setSubmittingCheckpoint] = useState(false);

  // Tab 3: Integrity Incidents
  const [incidents, setIncidents] = useState([]);
  const [incidentsLoading, setIncidentsLoading] = useState(false);
  const [selectedIncidentForAck, setSelectedIncidentForAck] = useState(null);
  const [operatorAckComment, setOperatorAckComment] = useState('');
  const [submittingAck, setSubmittingAck] = useState(false);

  // Tab 4: Raw Audit Logs
  const [auditLogs, setAuditLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [tenantFilter, setTenantFilter] = useState('');
  const [limit, setLimit] = useState(50);
  const [verifiedEventMap, setVerifiedEventMap] = useState({});
  const [verifyingAuditId, setVerifyingAuditId] = useState(null);

  // General alert/notification banner
  const [feedback, setFeedback] = useState(null);

  // Load Stream Summaries
  const loadStreams = async () => {
    setStreamsLoading(true);
    setStreamError(null);
    try {
      const res = await saasPlatformService.getStreamIntegritySummaries();
      setStreams(res?.data || (Array.isArray(res) ? res : []));
    } catch (err) {
      console.error('Failed to load audit stream summaries', err);
      setStreamError(err?.response?.data?.message || err?.message || 'Failed to retrieve audit streams.');
    } finally {
      setStreamsLoading(false);
    }
  };

  // Load Checkpoints
  const loadCheckpoints = async () => {
    setCheckpointsLoading(true);
    try {
      const res = await saasPlatformService.getIntegrityCheckpoints();
      setCheckpoints(res?.data || (Array.isArray(res) ? res : []));
    } catch (err) {
      console.error('Failed to load integrity checkpoints', err);
    } finally {
      setCheckpointsLoading(false);
    }
  };

  // Load Incidents
  const loadIncidents = async () => {
    setIncidentsLoading(true);
    try {
      const res = await saasPlatformService.getIntegrityIncidents();
      setIncidents(res?.data || (Array.isArray(res) ? res : []));
    } catch (err) {
      console.error('Failed to load integrity incidents', err);
    } finally {
      setIncidentsLoading(false);
    }
  };

  // Load Audit Logs
  const loadAuditLogs = async () => {
    setLogsLoading(true);
    try {
      const tId = tenantFilter ? parseInt(tenantFilter, 10) : null;
      const res = await saasPlatformService.getAuditLogs(tId, limit);
      setAuditLogs(res?.data || (Array.isArray(res) ? res : []));
    } catch (err) {
      console.error('Failed to load platform audit logs', err);
    } finally {
      setLogsLoading(false);
    }
  };

  // Initial tab loading
  useEffect(() => {
    if (activeTab === 'streams') loadStreams();
    else if (activeTab === 'checkpoints') loadCheckpoints();
    else if (activeTab === 'incidents') loadIncidents();
    else if (activeTab === 'logs') loadAuditLogs();
  }, [activeTab]);

  // Run on-demand stream verification
  const handleVerifyStream = async (streamId) => {
    setVerifyingStreamId(streamId);
    setFeedback(null);
    try {
      const res = await saasPlatformService.verifyStreamIntegrity(streamId);
      const report = res?.data || res;
      setSelectedVerificationReport(report);
      // Reload stream summaries to reflect verification status
      loadStreams();
    } catch (err) {
      console.error(`Failed to verify stream ${streamId}`, err);
      setFeedback({ type: 'error', message: `Verification failed for stream ${streamId}: ${err?.response?.data?.message || err?.message}` });
    } finally {
      setVerifyingStreamId(null);
    }
  };

  // Export Sanitized Manifest
  const handleExportManifest = async (streamId) => {
    try {
      const res = await saasPlatformService.getIntegrityReport(streamId);
      const data = res?.data || res;
      const jsonStr = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `integrity-manifest-${streamId}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export manifest', err);
      setFeedback({ type: 'error', message: `Export failed: ${err?.response?.data?.message || err?.message}` });
    }
  };

  // Create Certified Checkpoint
  const handleCreateCheckpoint = async (e) => {
    e.preventDefault();
    setSubmittingCheckpoint(true);
    setFeedback(null);
    try {
      const res = await saasPlatformService.createIntegrityCheckpoint({
        streamId: checkpointStreamId,
        notes: checkpointNotes
      });
      setShowCheckpointModal(false);
      setCheckpointNotes('');
      setFeedback({ type: 'success', message: `Successfully created certified checkpoint #${res?.data?.checkpointId || ''} for ${checkpointStreamId}` });
      loadCheckpoints();
    } catch (err) {
      console.error('Failed to create checkpoint', err);
      setFeedback({ type: 'error', message: `Failed to create checkpoint: ${err?.response?.data?.message || err?.message}` });
    } finally {
      setSubmittingCheckpoint(false);
    }
  };

  // Acknowledge Incident
  const handleAcknowledgeIncident = async (e) => {
    e.preventDefault();
    if (!selectedIncidentForAck || !operatorAckComment.trim()) return;
    setSubmittingAck(true);
    setFeedback(null);
    try {
      await saasPlatformService.acknowledgeIntegrityIncident(
        selectedIncidentForAck.incidentId,
        operatorAckComment.trim()
      );
      setSelectedIncidentForAck(null);
      setOperatorAckComment('');
      setFeedback({ type: 'success', message: `Integrity incident #${selectedIncidentForAck.incidentId} acknowledged.` });
      loadIncidents();
    } catch (err) {
      console.error('Failed to acknowledge incident', err);
      setFeedback({ type: 'error', message: `Failed to acknowledge incident: ${err?.response?.data?.message || err?.message}` });
    } finally {
      setSubmittingAck(false);
    }
  };

  // Verify Single Event
  const handleVerifyEvent = async (auditId) => {
    setVerifyingAuditId(auditId);
    try {
      const res = await saasPlatformService.verifySingleEvent(auditId);
      const result = res?.data || res;
      setVerifiedEventMap((prev) => ({ ...prev, [auditId]: result }));
    } catch (err) {
      console.error(`Failed to verify event ${auditId}`, err);
      setVerifiedEventMap((prev) => ({
        ...prev,
        [auditId]: { status: 'INVALID', sanitizedSummary: err?.response?.data?.message || 'Verification error' }
      }));
    } finally {
      setVerifyingAuditId(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'VALID':
        return <span className="saas-badge saas-badge-active">✔ VALID</span>;
      case 'INVALID':
        return <span className="saas-badge saas-badge-suspended">✖ TAMPERED</span>;
      case 'INCOMPLETE':
        return <span className="saas-badge saas-badge-provisioning">⚠ INCOMPLETE</span>;
      case 'LEGACY_UNVERIFIED':
      default:
        return <span className="saas-badge saas-badge-info" style={{ background: '#f1f5f9', color: '#64748b', borderColor: '#cbd5e1' }}>⏳ UNVERIFIED</span>;
    }
  };

  return (
    <div className="saas-container">
      {/* Page Header */}
      <div className="saas-header">
        <div className="saas-header-title">
          <h1>
            <span>🛡️</span> Tamper-Evident Audit Trails &amp; Hash Chain Verification
          </h1>
          <p>Cryptographic SHA-256 Hash Chaining, Multi-Tenant Stream Partitioning &amp; Integrity Attestation</p>
        </div>
        <div className="saas-header-actions">
          {activeTab === 'streams' && (
            <button className="saas-btn saas-btn-secondary" onClick={loadStreams} disabled={streamsLoading}>
              🔄 Refresh Streams
            </button>
          )}
          {activeTab === 'checkpoints' && (
            <>
              <button className="saas-btn saas-btn-secondary" onClick={loadCheckpoints} disabled={checkpointsLoading}>
                🔄 Refresh
              </button>
              <button className="saas-btn saas-btn-primary" onClick={() => setShowCheckpointModal(true)}>
                ✍️ Create Certified Checkpoint
              </button>
            </>
          )}
          {activeTab === 'incidents' && (
            <button className="saas-btn saas-btn-secondary" onClick={loadIncidents} disabled={incidentsLoading}>
              🔄 Refresh Incidents
            </button>
          )}
          {activeTab === 'logs' && (
            <button className="saas-btn saas-btn-secondary" onClick={loadAuditLogs} disabled={logsLoading}>
              🔄 Refresh Logs
            </button>
          )}
        </div>
      </div>

      {/* Global Alerts */}
      {feedback && (
        <div className={`saas-${feedback.type === 'error' ? 'error-box' : 'success-box'}`} style={{ marginBottom: '1.5rem' }}>
          <div>{feedback.message}</div>
          <button className="saas-btn saas-btn-secondary" style={{ padding: '0.2rem 0.5rem' }} onClick={() => setFeedback(null)}>
            Dismiss
          </button>
        </div>
      )}

      {/* Metric Cards */}
      <div className="saas-stats-grid">
        <div className="saas-stat-card">
          <span className="saas-stat-label">Active Audit Streams</span>
          <span className="saas-stat-value">{streams.length}</span>
          <span className="saas-stat-subtext">Platform &amp; tenant partitions</span>
        </div>
        <div className="saas-stat-card">
          <span className="saas-stat-label">Verified Streams</span>
          <span className="saas-stat-value" style={{ color: '#047857' }}>
            {streams.filter((s) => s.integrityStatus === 'VALID').length}
          </span>
          <span className="saas-stat-subtext">Passed SHA-256 chain verification</span>
        </div>
        <div className="saas-stat-card">
          <span className="saas-stat-label">Open Integrity Incidents</span>
          <span className="saas-stat-value" style={{ color: incidents.filter((i) => i.status === 'OPEN').length > 0 ? '#b91c1c' : '#047857' }}>
            {incidents.filter((i) => i.status === 'OPEN').length}
          </span>
          <span className="saas-stat-subtext">Tampering / chain breaks</span>
        </div>
        <div className="saas-stat-card">
          <span className="saas-stat-label">Certified Checkpoints</span>
          <span className="saas-stat-value" style={{ color: '#0284c7' }}>
            {checkpoints.length}
          </span>
          <span className="saas-stat-subtext">Signed chain-head attestations</span>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', borderBottom: '2px solid #e2e8f0', marginBottom: '1.5rem' }}>
        <button
          className="saas-btn"
          style={{
            background: 'none',
            borderBottom: activeTab === 'streams' ? '2px solid #0284c7' : 'none',
            borderRadius: '0',
            color: activeTab === 'streams' ? '#0284c7' : '#64748b',
            fontWeight: activeTab === 'streams' ? '700' : '500'
          }}
          onClick={() => setActiveTab('streams')}
        >
          ⛓️ Stream Integrity &amp; Chains
        </button>
        <button
          className="saas-btn"
          style={{
            background: 'none',
            borderBottom: activeTab === 'checkpoints' ? '2px solid #0284c7' : 'none',
            borderRadius: '0',
            color: activeTab === 'checkpoints' ? '#0284c7' : '#64748b',
            fontWeight: activeTab === 'checkpoints' ? '700' : '500'
          }}
          onClick={() => setActiveTab('checkpoints')}
        >
          🔖 Certified Checkpoints ({checkpoints.length})
        </button>
        <button
          className="saas-btn"
          style={{
            background: 'none',
            borderBottom: activeTab === 'incidents' ? '2px solid #0284c7' : 'none',
            borderRadius: '0',
            color: activeTab === 'incidents' ? '#0284c7' : '#64748b',
            fontWeight: activeTab === 'incidents' ? '700' : '500'
          }}
          onClick={() => setActiveTab('incidents')}
        >
          🚨 Integrity Incidents ({incidents.filter((i) => i.status === 'OPEN').length})
        </button>
        <button
          className="saas-btn"
          style={{
            background: 'none',
            borderBottom: activeTab === 'logs' ? '2px solid #0284c7' : 'none',
            borderRadius: '0',
            color: activeTab === 'logs' ? '#0284c7' : '#64748b',
            fontWeight: activeTab === 'logs' ? '700' : '500'
          }}
          onClick={() => setActiveTab('logs')}
        >
          📜 Audit Log Stream
        </button>
      </div>

      {/* TAB 1: Stream Integrity & Hash Chains */}
      {activeTab === 'streams' && (
        <>
          {streamError && (
            <div className="saas-error-box" style={{ marginBottom: '1.5rem' }}>
              <div><strong>Error:</strong> {streamError}</div>
              <button className="saas-btn saas-btn-secondary" onClick={loadStreams}>Retry</button>
            </div>
          )}

          {/* Verification Results Panel (if report generated) */}
          {selectedVerificationReport && (
            <div className="saas-card" style={{ borderColor: selectedVerificationReport.status === 'VALID' ? '#a7f3d0' : '#fecaca', marginBottom: '1.5rem' }}>
              <div className="saas-card-header" style={{ background: selectedVerificationReport.status === 'VALID' ? '#ecfdf5' : '#fef2f2' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <h3>Verification Report: {selectedVerificationReport.streamId}</h3>
                  {getStatusBadge(selectedVerificationReport.status)}
                </div>
                <button
                  className="saas-btn saas-btn-secondary"
                  style={{ padding: '0.2rem 0.6rem' }}
                  onClick={() => setSelectedVerificationReport(null)}
                >
                  Close Report ✕
                </button>
              </div>
              <div className="saas-card-body">
                <p style={{ margin: '0 0 1rem 0', fontWeight: '500' }}>{selectedVerificationReport.sanitizedSummary}</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div style={{ padding: '0.75rem', background: '#f8fafc', borderRadius: '6px' }}>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase' }}>Total Examined</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{selectedVerificationReport.totalExamined}</div>
                  </div>
                  <div style={{ padding: '0.75rem', background: '#ecfdf5', borderRadius: '6px' }}>
                    <div style={{ fontSize: '0.75rem', color: '#047857', textTransform: 'uppercase' }}>Valid Events</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#047857' }}>{selectedVerificationReport.validCount}</div>
                  </div>
                  <div style={{ padding: '0.75rem', background: '#fef2f2', borderRadius: '6px' }}>
                    <div style={{ fontSize: '0.75rem', color: '#b91c1c', textTransform: 'uppercase' }}>Tampered Events</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#b91c1c' }}>{selectedVerificationReport.tamperedCount}</div>
                  </div>
                  <div style={{ padding: '0.75rem', background: '#fffbeb', borderRadius: '6px' }}>
                    <div style={{ fontSize: '0.75rem', color: '#b45309', textTransform: 'uppercase' }}>Sequence Gaps</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#b45309' }}>{selectedVerificationReport.missingSequenceCount}</div>
                  </div>
                  <div style={{ padding: '0.75rem', background: '#f1f5f9', borderRadius: '6px' }}>
                    <div style={{ fontSize: '0.75rem', color: '#475569', textTransform: 'uppercase' }}>Legacy Unverified</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#475569' }}>{selectedVerificationReport.legacyUnverifiedCount}</div>
                  </div>
                </div>

                {selectedVerificationReport.incidents && selectedVerificationReport.incidents.length > 0 && (
                  <div>
                    <h4 style={{ color: '#b91c1c', margin: '0 0 0.5rem 0' }}>Detected Integrity Violations:</h4>
                    <table className="saas-table">
                      <thead>
                        <tr>
                          <th>Seq</th>
                          <th>Category</th>
                          <th>Diagnostic Details</th>
                          <th>Expected Hash</th>
                          <th>Actual Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedVerificationReport.incidents.map((inc, idx) => (
                          <tr key={idx}>
                            <td>{inc.sequenceNumber}</td>
                            <td><span className="saas-badge saas-badge-suspended">{inc.failureCategory}</span></td>
                            <td style={{ color: '#b91c1c' }}>{inc.sanitizedDiagnostics}</td>
                            <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{inc.expectedValue?.slice(0, 16)}...</td>
                            <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{inc.actualValue?.slice(0, 16)}...</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Streams Table */}
          <div className="saas-card">
            <div className="saas-card-header">
              <h3>Partitioned Audit Chains ({streams.length})</h3>
            </div>
            <div className="saas-table-container">
              <table className="saas-table">
                <thead>
                  <tr>
                    <th>Stream ID</th>
                    <th>Partition Scope</th>
                    <th>Last Sequence</th>
                    <th>Total Events</th>
                    <th>Current Chain-Head Hash</th>
                    <th>Last Activity</th>
                    <th>Integrity Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {streamsLoading ? (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '3rem' }}>
                        ⏳ Inspecting Multi-Tenant Chain Heads...
                      </td>
                    </tr>
                  ) : streams.length > 0 ? (
                    streams.map((s) => (
                      <tr key={s.streamId}>
                        <td style={{ fontFamily: 'monospace', fontWeight: '600' }}>{s.streamId}</td>
                        <td>
                          {s.tenantId ? (
                            <span className="saas-badge saas-badge-info">Tenant #{s.tenantId}</span>
                          ) : (
                            <span style={{ color: '#64748b', fontWeight: '500' }}>Platform Core</span>
                          )}
                        </td>
                        <td>#{s.lastSequenceNumber}</td>
                        <td>{s.totalEvents}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#475569' }} title={s.lastRecordHash}>
                          {s.lastRecordHash ? `${s.lastRecordHash.slice(0, 12)}...${s.lastRecordHash.slice(-8)}` : 'GENESIS'}
                        </td>
                        <td style={{ color: '#64748b', fontSize: '0.85rem' }}>
                          {s.updatedAtUtc ? new Date(s.updatedAtUtc).toLocaleString() : '—'}
                        </td>
                        <td>{getStatusBadge(s.integrityStatus)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.4rem' }}>
                            <button
                              className="saas-btn saas-btn-primary"
                              style={{ padding: '0.25rem 0.6rem', fontSize: '0.8rem' }}
                              onClick={() => handleVerifyStream(s.streamId)}
                              disabled={verifyingStreamId === s.streamId}
                            >
                              {verifyingStreamId === s.streamId ? 'Verifying...' : '⚡ Verify Chain'}
                            </button>
                            <button
                              className="saas-btn saas-btn-secondary"
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                              onClick={() => handleExportManifest(s.streamId)}
                              title="Export Sanitized Verification Manifest"
                            >
                              📥 Export
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                        No audit chains initialized yet. Operations on platform and tenants will advance the chain head.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* TAB 2: Certified Checkpoints */}
      {activeTab === 'checkpoints' && (
        <>
          {/* Trust Anchor Architecture Callout */}
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '1rem 1.25rem', marginBottom: '1.5rem' }}>
            <h4 style={{ color: '#b45309', margin: '0 0 0.25rem 0' }}>🔐 Checkpoint Trust Anchor Classification</h4>
            <p style={{ color: '#78350f', fontSize: '0.875rem', margin: 0 }}>
              Checkpoints are currently certified using an HMAC-SHA256 application-level trust anchor. While application database triggers protect historical records from unauthorized in-database modification, full mathematical non-repudiation against an infrastructure superuser requires an external Cloud KMS or HSM trust anchor.
            </p>
          </div>

          <div className="saas-card">
            <div className="saas-card-header">
              <h3>Certified Chain Checkpoints ({checkpoints.length})</h3>
            </div>
            <div className="saas-table-container">
              <table className="saas-table">
                <thead>
                  <tr>
                    <th>Checkpoint ID</th>
                    <th>Stream ID</th>
                    <th>Sequence</th>
                    <th>Chain Head Hash</th>
                    <th>Signed Checksum</th>
                    <th>Signed By</th>
                    <th>Trust Anchor</th>
                    <th>Verified Events</th>
                    <th>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {checkpointsLoading ? (
                    <tr>
                      <td colSpan="9" style={{ textAlign: 'center', padding: '3rem' }}>
                        ⏳ Loading Checkpoints...
                      </td>
                    </tr>
                  ) : checkpoints.length > 0 ? (
                    checkpoints.map((cp) => (
                      <tr key={cp.checkpointId}>
                        <td style={{ fontFamily: 'monospace' }}>#{cp.checkpointId}</td>
                        <td><strong>{cp.streamId}</strong></td>
                        <td>Seq #{cp.sequenceNumber}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }} title={cp.recordHash}>
                          {cp.recordHash?.slice(0, 10)}...
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#047857' }} title={cp.signedChecksum}>
                          {cp.signedChecksum?.slice(0, 12)}...
                        </td>
                        <td>{cp.signedBy}</td>
                        <td>
                          <span className="saas-badge saas-badge-info" style={{ fontSize: '0.7rem' }}>
                            {cp.trustAnchorType}
                          </span>
                        </td>
                        <td>{cp.verifiedCount}</td>
                        <td style={{ color: '#64748b', fontSize: '0.85rem' }}>
                          {new Date(cp.checkpointTimestampUtc).toLocaleString()}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                        No certified checkpoints recorded. Use "Create Certified Checkpoint" to freeze a verified chain head.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* TAB 3: Integrity Incidents Inbox */}
      {activeTab === 'incidents' && (
        <div className="saas-card">
          <div className="saas-card-header">
            <h3>Chain Integrity Violations &amp; Incidents ({incidents.length})</h3>
          </div>
          <div className="saas-table-container">
            <table className="saas-table">
              <thead>
                <tr>
                  <th>Incident ID</th>
                  <th>Stream ID</th>
                  <th>Sequence</th>
                  <th>Failure Category</th>
                  <th>Sanitized Diagnostics</th>
                  <th>Detected At</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {incidentsLoading ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '3rem' }}>
                      ⏳ Scanning Integrity Incidents...
                    </td>
                  </tr>
                ) : incidents.length > 0 ? (
                  incidents.map((inc) => (
                    <tr key={inc.incidentId}>
                      <td style={{ fontFamily: 'monospace' }}>#{inc.incidentId}</td>
                      <td><strong>{inc.streamId}</strong></td>
                      <td>Seq #{inc.sequenceNumber}</td>
                      <td>
                        <span className="saas-badge saas-badge-suspended">{inc.failureCategory}</span>
                      </td>
                      <td style={{ color: '#b91c1c', fontSize: '0.85rem' }}>{inc.sanitizedDiagnostics}</td>
                      <td style={{ color: '#64748b', fontSize: '0.85rem' }}>
                        {new Date(inc.detectedAtUtc).toLocaleString()}
                      </td>
                      <td>
                        {inc.status === 'OPEN' ? (
                          <span className="saas-badge saas-badge-suspended">🔴 OPEN</span>
                        ) : (
                          <span className="saas-badge saas-badge-active">✔ ACKNOWLEDGED</span>
                        )}
                      </td>
                      <td>
                        {inc.status === 'OPEN' ? (
                          <button
                            className="saas-btn saas-btn-secondary"
                            style={{ padding: '0.25rem 0.6rem', fontSize: '0.8rem' }}
                            onClick={() => setSelectedIncidentForAck(inc)}
                          >
                            Acknowledge
                          </button>
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                            Ack by {inc.acknowledgedBy || 'Operator'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '3rem', color: '#047857' }}>
                      ✔ Clean Bill of Health! Zero chain tampering or sequence violation incidents recorded.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 4: Raw Audit Log Stream */}
      {activeTab === 'logs' && (
        <>
          <div className="saas-filters">
            <input
              type="number"
              className="saas-search-input"
              style={{ maxWidth: '200px' }}
              placeholder="Filter by Tenant ID..."
              value={tenantFilter}
              onChange={(e) => setTenantFilter(e.target.value)}
            />
            <select
              className="saas-select"
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value, 10))}
            >
              <option value={25}>Show 25 Logs</option>
              <option value={50}>Show 50 Logs</option>
              <option value={100}>Show 100 Logs</option>
            </select>
            <button className="saas-btn saas-btn-primary" onClick={loadAuditLogs}>
              🔍 Apply Filter
            </button>
          </div>

          <div className="saas-card">
            <div className="saas-card-header">
              <h3>Audit Log Records ({auditLogs.length})</h3>
            </div>
            <div className="saas-table-container">
              <table className="saas-table">
                <thead>
                  <tr>
                    <th>Audit ID</th>
                    <th>Stream &amp; Seq</th>
                    <th>Action</th>
                    <th>Actor</th>
                    <th>Details</th>
                    <th>Hash Chain Status</th>
                    <th>Timestamp</th>
                    <th>Verification</th>
                  </tr>
                </thead>
                <tbody>
                  {logsLoading ? (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '3rem' }}>
                        ⏳ Querying Platform Audit Log...
                      </td>
                    </tr>
                  ) : auditLogs.length > 0 ? (
                    auditLogs.map((log) => {
                      const eventId = log.auditID;
                      const checkResult = verifiedEventMap[eventId];
                      return (
                        <tr key={eventId}>
                          <td style={{ fontFamily: 'monospace' }}>#{eventId}</td>
                          <td>
                            <div style={{ fontSize: '0.85rem', fontWeight: '600' }}>
                              {log.streamID || 'PLATFORM_ROOT'}
                            </div>
                            {log.sequenceNumber ? (
                              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Seq #{log.sequenceNumber}</div>
                            ) : (
                              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Legacy Pre-Chain</span>
                            )}
                          </td>
                          <td>
                            <span
                              className={
                                log.action.includes('SUSPEND')
                                  ? 'saas-badge saas-badge-suspended'
                                  : log.action.includes('ONBOARD') || log.action.includes('REACTIVATE')
                                  ? 'saas-badge saas-badge-active'
                                  : 'saas-badge saas-badge-info'
                              }
                            >
                              {log.action}
                            </span>
                          </td>
                          <td><strong>{log.actorUsername}</strong></td>
                          <td style={{ color: '#475569', fontSize: '0.85rem' }}>{log.details || '—'}</td>
                          <td>{getStatusBadge(log.integrityStatus)}</td>
                          <td style={{ color: '#64748b', fontSize: '0.85rem' }}>
                            {new Date(log.createdAt).toLocaleString()}
                          </td>
                          <td>
                            {checkResult ? (
                              checkResult.status === 'VALID' ? (
                                <span className="saas-badge saas-badge-active" title={checkResult.sanitizedSummary}>✔ Intact</span>
                              ) : (
                                <span className="saas-badge saas-badge-suspended" title={checkResult.sanitizedSummary}>✖ Tampered</span>
                              )
                            ) : (
                              <button
                                className="saas-btn saas-btn-secondary"
                                style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                                onClick={() => handleVerifyEvent(eventId)}
                                disabled={verifyingAuditId === eventId}
                              >
                                {verifyingAuditId === eventId ? '...' : 'Verify'}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                        No audit logs recorded matching your filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Modal: Create Certified Checkpoint */}
      {showCheckpointModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#ffffff', borderRadius: '8px', width: '90%', maxWidth: '500px', padding: '1.5rem', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <h3 style={{ margin: '0 0 1rem 0' }}>✍️ Create Certified Integrity Checkpoint</h3>
            <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1rem' }}>
              Freezes and cryptographically signs the current chain head. Only valid, unbroken audit streams can be checkpointed.
            </p>
            <form onSubmit={handleCreateCheckpoint}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', marginBottom: '0.35rem' }}>
                  Target Audit Stream
                </label>
                <select
                  className="saas-select"
                  style={{ width: '100%' }}
                  value={checkpointStreamId}
                  onChange={(e) => setCheckpointStreamId(e.target.value)}
                >
                  <option value="PLATFORM_ROOT">PLATFORM_ROOT (Platform Stream)</option>
                  {streams.filter((s) => s.streamId !== 'PLATFORM_ROOT').map((s) => (
                    <option key={s.streamId} value={s.streamId}>{s.streamId} (Tenant #{s.tenantId})</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', marginBottom: '0.35rem' }}>
                  Operator Attestation Notes
                </label>
                <textarea
                  className="saas-search-input"
                  style={{ width: '100%', minHeight: '80px' }}
                  placeholder="e.g. Scheduled periodic audit checkpoint before system maintenance"
                  value={checkpointNotes}
                  onChange={(e) => setCheckpointNotes(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                <button
                  type="button"
                  className="saas-btn saas-btn-secondary"
                  onClick={() => setShowCheckpointModal(false)}
                  disabled={submittingCheckpoint}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="saas-btn saas-btn-primary"
                  disabled={submittingCheckpoint}
                >
                  {submittingCheckpoint ? 'Signing...' : 'Sign Checkpoint'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Acknowledge Integrity Incident */}
      {selectedIncidentForAck && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#ffffff', borderRadius: '8px', width: '90%', maxWidth: '500px', padding: '1.5rem', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#b91c1c' }}>🚨 Acknowledge Integrity Incident #{selectedIncidentForAck.incidentId}</h3>
            <p style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '0.5rem' }}>
              <strong>Category:</strong> {selectedIncidentForAck.failureCategory} on stream <code>{selectedIncidentForAck.streamId}</code> (Seq #{selectedIncidentForAck.sequenceNumber})
            </p>
            <p style={{ fontSize: '0.8125rem', color: '#b91c1c', marginBottom: '1rem' }}>
              {selectedIncidentForAck.sanitizedDiagnostics}
            </p>
            <form onSubmit={handleAcknowledgeIncident}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', marginBottom: '0.35rem' }}>
                  Operator Justification / Commentary (Required)
                </label>
                <textarea
                  className="saas-search-input"
                  style={{ width: '100%', minHeight: '80px' }}
                  placeholder="Record your investigation findings and remediation steps taken..."
                  value={operatorAckComment}
                  onChange={(e) => setOperatorAckComment(e.target.value)}
                  required
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                <button
                  type="button"
                  className="saas-btn saas-btn-secondary"
                  onClick={() => setSelectedIncidentForAck(null)}
                  disabled={submittingAck}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="saas-btn saas-btn-danger"
                  disabled={submittingAck || !operatorAckComment.trim()}
                >
                  {submittingAck ? 'Recording...' : 'Confirm Acknowledgment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformAudit;
